/**
 * Fetches the organizer-uploaded raceline GeoJSON for an event and
 * precomputes the cumulative-s curve the viewer needs for trimming,
 * stage-progress %, and cross-trajectory projection.
 *
 * Returns null when:
 *   - eventId is null, or
 *   - the event's `show_raceline` is false, or
 *   - the backend 404s the GET (no upload yet).
 *
 * Callers should pass `enabled = event.show_raceline` so the hook
 * short-circuits without an HTTP call when the toggle is off.
 */

import { useEffect, useState } from 'react';

import { racelineUrl } from '../services/fleet';
import { cumulativeSMeters, smoothCatmullRom } from '../plugins/synced_viewer/util';

export interface RacelineData {
  /** Raw GeoJSON LineString — kept around so a future cross-trajectory hook
   * can hand it to turf without rebuilding the geometry. */
  geojson: { type: 'LineString'; coordinates: Array<[number, number]> };
  /** Vertex list in [lat, lon] order — what react-leaflet's Polyline wants. */
  coords: Array<[number, number]>;
  /** cumulative_s_m[i] = meters from coords[0] to coords[i]. */
  cumulativeS: number[];
  totalLengthM: number;
}

export function useEventRaceline(
  eventId: number | null,
  enabled: boolean,
): { raceline: RacelineData | null; loading: boolean } {
  const [raceline, setRaceline] = useState<RacelineData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || eventId == null) {
      setRaceline(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(racelineUrl(eventId));
        if (!res.ok) {
          if (!cancelled) setRaceline(null);
          return;
        }
        const data = await res.json();
        const lonlat: Array<[number, number]> = Array.isArray(data?.coordinates)
          ? data.coordinates
          : Array.isArray(data?.geometry?.coordinates)
            ? data.geometry.coordinates
            : [];
        const rawLatlon: Array<[number, number]> = lonlat
          .filter((p) => Array.isArray(p) && p.length >= 2)
          .map(([lon, lat]) => [lat, lon] as [number, number]);
        if (rawLatlon.length < 2) {
          if (!cancelled) setRaceline(null);
          return;
        }
        // Catmull-Rom smooth + ~2.5 m resample so hand-clicked polygon
        // corners render as real track curves. Applied at the data
        // layer (not per-renderer) so the map overlay, mini-map, and
        // the cumulative-s / cross-trajectory projection all see the
        // SAME geometry — the spline interpolates through every
        // original vertex, so s-projections only get denser, never
        // displaced.
        const latlon = smoothCatmullRom(rawLatlon);
        const cumulativeS = cumulativeSMeters(latlon);
        const totalLengthM = cumulativeS[cumulativeS.length - 1] ?? 0;
        if (!cancelled) {
          setRaceline({
            geojson: {
              type: 'LineString',
              coordinates: latlon.map(([lat, lon]) => [lon, lat] as [number, number]),
            },
            coords: latlon,
            cumulativeS,
            totalLengthM,
          });
        }
      } catch (err) {
        console.warn('[useEventRaceline] fetch failed', err);
        if (!cancelled) setRaceline(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, enabled]);

  return { raceline, loading };
}

export default useEventRaceline;
